<template>
  <div>
    <CRow>
      <CCol sm="12">
        <CCard>
          <CCardBody class="p-4">
            <CButton color="link" class="px-0 mb-2" @click="cancel">
              <CIcon name="cil-arrow-left" /> Transactions
            </CButton>
            <h1 class="h4 mb-1 font-weight-bold">
              {{ obj.id ? obj.description || "Transaction" : "Add transaction" }}
            </h1>
            <p class="text-muted mb-4">
              Record money in or out and link it to an account, invoice or expense
            </p>
            <CForm>
              <CInput
                type="date"
                :value="computeTransactionDate"
                @change="setTransactionDate"
                label="Date *"
                horizontal
              />
              <CInput
                label="Description"
                v-model="obj.description"
                horizontal
              />
              <CInput
                label="Amount *"
                v-model="obj.amount"
                type="number"
                inputmode="decimal"
                step="0.01"
                prepend="RM"
                horizontal
              />
              <CSelect
                horizontal
                :value.sync="selectedTransactionType"
                :options="transactionTypes"
                label="Transaction type"
              />
              <CSelect
                horizontal
                :value.sync="selectedTransactionSourceType"
                :options="transactionSourceTypes"
                label="Source"
              />
              <CInput
                v-if="selectedTransactionSourceType == 1"
                label="Bank statement"
                horizontal
                readonly
                :value.sync="selectedBankStatementInfo"
              />
              <CRow form class="form-group" v-if="obj.bankStatement">
                <CCol tag="label" sm="3" class="col-form-label"> </CCol>
                <CCol sm="9">
                  <a
                    :href="getDocumentUrl(obj.bankStatement)"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    Open bank statement in a new tab
                  </a>
                </CCol>
              </CRow>
              <CRow form class="form-group">
                <CCol tag="label" sm="3" class="col-form-label" for="txn-account">
                  Account
                </CCol>
                <CCol sm="9">
                  <v-select
                    input-id="txn-account"
                    class="w-100"
                    v-model="selectedChartOfAccount"
                    :label="`itemDisplay`"
                    :options="computedChartAccountItems"
                    placeholder="Choose an account"
                  />
                </CCol>
              </CRow>
              <CSelect
                horizontal
                :value.sync="selectedTransactionMatchType"
                :options="transactionMatchTypes"
                label="Match to"
              />
              <CInput
                v-if="isShowMatchDetails"
                label="Matched record"
                horizontal
                readonly
                :value.sync="selectedMatchInfo"
              >
                <template #append>
                  <CButton color="primary" @click="onSearchMatch()">
                    Choose
                  </CButton>
                </template>
              </CInput>
              <CTextarea
                label="Remarks"
                horizontal
                rows="4"
                v-model="obj.remarks"
              />
            </CForm>
          </CCardBody>
          <CCardFooter class="d-flex justify-content-between">
            <CButton color="light" :disabled="saving" @click="cancel">
              Cancel
            </CButton>
            <CButton color="primary" :disabled="saving" @click="submit">
              <CSpinner v-if="saving" size="sm" class="mr-1" />
              {{ obj.id ? "Save" : "Add transaction" }}
            </CButton>
          </CCardFooter>
        </CCard>
      </CCol>
    </CRow>
    <CModal
      :title="selectedTransactionMatchType == 1 ? 'Choose an expense' : 'Choose an invoice'"
      :show.sync="matchSearchPopup"
      size="lg"
      centered
    >
      <div v-if="loading && !matchList.length" class="text-center p-4">
        <CSpinner color="primary" />
      </div>
      <div v-else-if="matchError" class="text-center p-4">
        <p class="text-muted">Couldn't load the list. Check your connection and try again.</p>
        <CButton color="primary" @click="refreshTableMatch">
          <CIcon name="cil-reload" /> Retry
        </CButton>
      </div>
      <div v-else-if="!matchList.length" class="text-center p-4 text-muted">
        Nothing to match yet.
      </div>
      <CDataTable
        v-else
        :items="matchList"
        :fields="matchFieldList"
        column-filter
        sorter
        pagination
        :items-per-page="10"
        :loading="loading"
      >
        <template #show_details="{ item }">
          <td class="py-2">
            <CButton
              color="primary"
              variant="outline"
              size="sm"
              @click="onMatchSelected(item)"
            >
              Select
            </CButton>
          </td>
        </template>
      </CDataTable>
      <template #footer>
        <CButton color="light" @click="matchSearchPopup = false">Cancel</CButton>
      </template>
    </CModal>
  </div>
</template>

<script>
import TransactionApi from "@/lib/transactionApi";
import ChartOfAccountApi from "../../lib/chartOfAccountApi";
import vSelect from "vue-select";
import "vue-select/dist/vue-select.css";
import InvoiceApi from "@/lib/invoiceApi";
import ExpenseApi from "@/lib/expenseApi";
import { asDateString, mytToday, parseDateInput } from "@/utils/datetime";

export default {
  name: "Transaction",
  components: {
    vSelect,
  },
  data: () => {
    return {
      expenseApi: new ExpenseApi(),
      invoiceApi: new InvoiceApi(),
      isShowMatchDetails: true,
      selectedMatch: null,
      matchSearchPopup: false,
      loading: false,
      matchError: false,
      saving: false,
      matchList: [],
      matchFieldList: [
        { key: "info", label: "Record" },
        {
          key: "show_details",
          label: "",
          _style: "width:1%",
          sorter: false,
          filter: false,
        },
      ],

      selectedChartOfAccount: null,
      chartOfAccountItems: [],
      chartOfAccountApi: new ChartOfAccountApi(),
      chartAccountItemsLoaded: false,

      transactionMatchTypes: [],
      selectedTransactionMatchType: 0,

      selectedTransactionSourceType: 0,
      transactionSourceTypes: [],
      transactionTypes: [],
      selectedTransactionType: null,
      obj: {
        date: mytToday(),
      },
      submitted: false,
      api: new TransactionApi(),
    };
  },
  mounted() {
    this.fetchTransactionTypes();
    this.fetchTransactionSourceTypes();
    this.fetchTransactionMatchTypes();
    this.fetchChartOfAccount();
    this.resetObj();
  },
  computed: {
    selectedBankStatementInfo() {
      if (this.obj.bankStatement != null) return this.obj.bankStatement.info;
      else return "";
    },
    selectedMatchInfo() {
      if (this.selectedMatch != null) return this.selectedMatch.info;
      else return "";
    },
    computedChartAccountItems() {
      return this.chartOfAccountItems.map((item) => {
        return {
          ...item,
          itemDisplay: `${item.accountNo} - ${
            item.category
          } - ${item.name.trim()}`,
        };
      });
    },

    computeTransactionDate() {
      return asDateString(this.obj.date);
    },
  },
  watch: {
    selectedTransactionMatchType(newVal) {
      this.isShowMatchDetails = newVal == 0 || newVal == 1;
    },
    chartAccountItemsLoaded(newVal) {
      // The account list can arrive after the transaction; pick its account then.
      if (newVal && this.obj.chartAccountId) {
        this.selectedChartOfAccount = this.findChartAccount(
          this.obj.chartAccountId
        );
      }
    },
  },
  methods: {
    getDocumentUrl(bankStatement) {
      try {
        return apiUrl + "documents/file/" + bankStatement.documentId;
      } catch (error) {
        return "";
      }
    },
    refreshTableMatch() {
      let req = null;
      if (this.selectedTransactionMatchType == 0) {
        req = this.invoiceApi.getListByCurrentBusiness();
      } else if (this.selectedTransactionMatchType == 1) {
        req = this.expenseApi.getListByCurrentBusiness();
      }
      if (!req) return;
      this.loading = true;
      this.matchError = false;
      req
        .then((response) => {
          this.matchList = response.result || [];
        })
        .catch((err) => {
          console.error(err);
          this.matchError = true;
        })
        .finally(() => {
          this.loading = false;
        });
    },
    onSearchMatch() {
      this.matchList = [];
      this.refreshTableMatch();
      this.matchSearchPopup = true;
    },

    onMatchSelected(item) {
      this.selectedMatch = item;

      // Match types: Invoice = 0, Expense = 1 (others not linked to a record here).
      if (this.selectedTransactionMatchType == 0) {
        this.obj.matchInvoiceId = item.id.toString();
      } else if (this.selectedTransactionMatchType == 1) {
        this.obj.matchExpenseId = item.id.toString();
      }

      this.matchSearchPopup = false;
    },

    findChartAccount(id) {
      return (
        this.computedChartAccountItems.find((item) => item.id === id) || null
      );
    },
    fetchChartOfAccount() {
      this.chartOfAccountApi
        .getListByCurrentBusiness()
        .then((response) => {
          this.chartOfAccountItems = response.result;
          this.chartAccountItemsLoaded = true;
        })
        .catch((err) => {
          console.error(err);
          this.$toast.error(
            "Couldn't load your chart of accounts. Reload the page to try again."
          );
        });
    },
    loadOptions(request, target, what) {
      request
        .then((response) => {
          this[target] = (response.result || []).map((state) => ({
            value: state.id,
            label: state.name,
          }));
        })
        .catch((err) => {
          console.error(err);
          this.$toast.error(
            `Couldn't load the ${what}. Reload the page to try again.`
          );
        });
    },
    fetchTransactionMatchTypes() {
      this.loadOptions(
        this.api.getTransactionMatchTypes(),
        "transactionMatchTypes",
        "match types"
      );
    },
    fetchTransactionSourceTypes() {
      this.loadOptions(
        this.api.getTransactionSourceTypes(),
        "transactionSourceTypes",
        "sources"
      );
    },
    fetchTransactionTypes() {
      this.loadOptions(
        this.api.getTransactionTypes(),
        "transactionTypes",
        "transaction types"
      );
    },
    setTransactionDate(e) {
      this.obj.date = parseDateInput(e);
    },

    cancel() {
      this.$router.push({ path: "/tenants/TransactionList" });
    },
    resetObj() {
      if (this.$route.params.id) {
        this.api
          .get(this.$route.params.id)
          .then((response) => {
            this.obj = response.result;
            if (this.chartAccountItemsLoaded)
              this.selectedChartOfAccount = this.findChartAccount(
                this.obj.chartAccountId
              );
            this.selectedTransactionType = this.obj.transactionType;
            this.selectedTransactionSourceType = this.obj.transactionSourceType;
            this.selectedTransactionMatchType = this.obj.transactionMatchType;

            if (this.obj.transactionMatchType == 0) {
              this.selectedTransactionMatchType = 0;
              this.selectedMatch = this.obj.invoice;
            } else if (this.obj.transactionMatchType == 1) {
              this.selectedTransactionMatchType = 1;
              this.selectedMatch = this.obj.expense;
            } else {
              this.selectedTransactionMatchType = -1;
              this.selectedMatch = null;
            }
          })
          .catch((err) => {
            console.error(err);
            this.$toast.error(
              "Couldn't load this transaction. Go back to the list and try again."
            );
          });
      } else {
        this.obj = this.getEmptyObj();
      }
    },
    onSubmit() {
      if (this.selectedChartOfAccount != null)
        this.obj.chartAccountId = this.selectedChartOfAccount.id;

      if (this.selectedMatch != null) {
        if (this.selectedTransactionMatchType == 0) {
          this.obj.matchInvoiceId = this.selectedMatch.id.toString();
        } else if (this.selectedTransactionMatchType == 1) {
          this.obj.matchExpenseId = this.selectedMatch.id.toString();
        }
      }

      if (this.selectedTransactionType != null)
        this.obj.transactionType = this.selectedTransactionType;
      if (this.selectedTransactionSourceType != null)
        this.obj.transactionSourceType = this.selectedTransactionSourceType;
      if (this.selectedTransactionMatchType != null)
        this.obj.transactionMatchType = this.selectedTransactionMatchType;

      const isNew = !this.obj.id;
      this.saving = true;
      const req = isNew ? this.api.create(this.obj) : this.api.update(this.obj);
      req
        .then(() => {
          this.$toast.success(isNew ? "Transaction added" : "Transaction saved");
          this.$router.push({ path: "/tenants/transactionList" });
        })
        .catch(({ data } = {}) => {
          this.$toast.error(
            `Couldn't save the transaction. ${helper.getErrorMessage(data)}`
          );
        })
        .finally(() => {
          this.saving = false;
        });
    },
    getEmptyObj() {
      return {
        date: mytToday(),
      };
    },
    submit() {
      if (this.saving) return;
      this.onSubmit();
      this.submitted = true;
    },
  },
};
</script>
